import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { WarningAmber as WarningIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { programAssignmentsApi, type AssignmentStatistics } from '@/api/programs'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'

/** Проценты рисуем одинаково везде: null — работ ещё не было, а не ноль */
function percentLabel(value: number | null): string {
  return value === null ? '—' : `${Math.round(value)}%`
}

/**
 * Вкладка «Статистика» карточки назначения.
 *
 * Учителю отвечает на два вопроса: какие темы не зашли группе и кому нужна
 * догоняющая работа. Ученику тот же эндпоинт отдаёт личный срез — там нет ни
 * состава группы, ни сравнения с одноклассниками, и вкладка сама об этом не
 * знает: решение принято на сервере.
 */
export function AssignmentStatisticsTab({
  assignmentId,
  completedAtByTheme,
}: {
  assignmentId: number
  /**
   * Даты прохождения тем назначения (id темы → момент ISO). Эндпоинт
   * статистики дат не отдаёт, а карточка назначения их уже загрузила.
   */
  completedAtByTheme?: ReadonlyMap<number, string>
}) {
  const { t } = useTranslation()

  const [stats, setStats] = useState<AssignmentStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      return await programAssignmentsApi.statistics(assignmentId)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))

      return null
    } finally {
      setLoading(false)
    }
  }, [assignmentId, t])

  useEffect(() => {
    let cancelled = false

    void load().then((result) => {
      if (cancelled || result === null) return
      setStats(result)
    })

    return () => {
      cancelled = true
    }
  }, [load])

  if (loading) {
    return <Skeleton variant="rounded" height={320} />
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (stats === null) {
    return null
  }

  const themeStatusLabel = (theme: AssignmentStatistics['themes'][number]): string => {
    const completedAt = theme.status === 'done' ? completedAtByTheme?.get(theme.theme_id) : undefined
    if (completedAt) return t('programs.completedOn', { date: formatDateDMY(completedAt) })

    return t(`programs.status.${theme.status === 'in_progress' ? 'inProgress' : theme.status}`)
  }

  const summary: Array<{ key: string; value: string }> = [
    { key: 'themes', value: `${stats.themes_done}/${stats.themes_total}` },
    { key: 'average', value: percentLabel(stats.average_percent) },
    { key: 'graded', value: String(stats.graded_count) },
    { key: 'awaiting', value: String(stats.awaiting_review) },
    { key: 'homework', value: `${stats.homework_done}/${stats.homework_total}` },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {summary.map((item) => (
              <Box key={item.key}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t(`programs.stats.summary.${item.key}`)}
                </Typography>
                <Typography variant="h6">{item.value}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('programs.stats.columns.theme')}</TableCell>
              <TableCell>{t('programs.stats.columns.progress')}</TableCell>
              <TableCell align="right">{t('programs.stats.columns.average')}</TableCell>
              <TableCell align="right">{t('programs.stats.columns.homework')}</TableCell>
              <TableCell align="right">
                <Tooltip title={t('programs.stats.timeHint')}>
                  <span>{t('programs.stats.columns.time')}</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.themes.map((theme) => (
              <TableRow key={theme.theme_id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {theme.position + 1}. {theme.title}
                    </Typography>
                    {theme.is_pit && (
                      <Tooltip
                        title={t('programs.stats.pitHint', { threshold: Math.round(stats.pit_threshold) })}
                      >
                        <WarningIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={themeStatusLabel(theme)} />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color={theme.is_pit ? 'warning.main' : 'text.primary'}>
                    {percentLabel(theme.average_percent)}
                  </Typography>
                  {theme.awaiting_review > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('programs.stats.awaitingShort', { count: theme.awaiting_review })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {theme.homework_total > 0
                    ? t('programs.stats.homeworkDone', {
                        done: theme.homework_done,
                        total: theme.homework_total,
                      })
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  {/* Главная цифра — занятия: она достоверна. Минуты считаются
                      по плановому слоту, поэтому идут второй строкой */}
                  {theme.lessons_count > 0 ? (
                    <>
                      <Typography variant="body2">
                        {t('programs.stats.lessonsCount', { count: theme.lessons_count })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('programs.stats.minutes', { count: theme.minutes })}
                      </Typography>
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {stats.students.length > 0 && (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('programs.stats.columns.student')}</TableCell>
                <TableCell sx={{ minWidth: 140 }}>{t('programs.stats.columns.average')}</TableCell>
                <TableCell align="right">{t('programs.stats.columns.homework')}</TableCell>
                <TableCell align="right">{t('programs.stats.columns.missed')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.students.map((student) => (
                <TableRow key={student.student_id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {student.name ?? '—'}
                      {student.left && (
                        <Chip size="small" variant="outlined" label={t('programs.stats.left')} />
                      )}
                      {student.below_average && (
                        <Chip size="small" color="warning" variant="outlined" label={t('programs.stats.behind')} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={student.average_percent ?? 0}
                        color={student.below_average ? 'warning' : 'primary'}
                        sx={{ flex: 1, height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                        {percentLabel(student.average_percent)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {student.homework_total > 0
                      ? t('programs.stats.homeworkDone', {
                          done: student.homework_done,
                          total: student.homework_total,
                        })
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {/* Только пропуски. Счётчик «догнал» тут всегда был нулём:
                        поставить отметку учителю негде — ни один экран не
                        показывает, какую именно тему кто пропустил (сервер
                        поле attendance темы наружу не отдаёт), а обещать в
                        цифрах то, чего в интерфейсе нет, нельзя */}
                    {student.missed_themes > 0 ? student.missed_themes : '—'}
                    {student.attendance_unknown > 0 && (
                      <Tooltip title={t('programs.stats.attendanceUnknownHint')}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('programs.stats.attendanceUnknown', { count: student.attendance_unknown })}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
